import React, { useEffect, useCallback, useMemo, useRef } from "react";
import { Message } from "../types/types";
import { formatBotResponseHTML } from "../helpers/formatBotResponseHTML";
import { ReactComponent as LightBulb } from "../images/lightbulb.svg";
import { ReactComponent as File } from "../images/file.svg";

type Props = {
  messages: Message[];
  onSubmit: (message?: string) => void;
  bottomRef: React.RefObject<HTMLDivElement | null>;
  isLoading: boolean;
  isThinking: boolean;
  setInputValue: (val: string) => void;
};

const ChatHistory: React.FC<Props> = ({
  messages,
  isLoading,
  isThinking,
  onSubmit,
  setInputValue,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastUserRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (lastUserRef.current) {
      lastUserRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [messages]);

  const handleHeaderClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const header = target.closest(".iris-tier2-header") as HTMLElement | null;
      if (!header) return;

      const content = header.nextElementSibling as HTMLElement | null;
      if (!content) return;

      content.classList.toggle("hidden");

      const icon = header.querySelector("i");
      icon?.classList.toggle("rotated");
    },
    [],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;

      // Prevent clicking buttons while bot is thinking/loading
      if (
        (isThinking || isLoading) &&
        target.tagName.toLowerCase() === "button"
      ) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }

      if (target.tagName.toLowerCase() === "button") {
        const text = target.textContent || "";
        setInputValue(text);
        onSubmit(text);
      }
    },
    [setInputValue, onSubmit, isThinking, isLoading],
  );

  const renderedMessages = useMemo(() => {
    let combinedBotMessages: string[] = [];
    let lastBotButtons: { text: string; value: string }[] = [];
    let lastFeedbackButtons: { text: string; value: string }[] = [];
    const result: React.ReactNode[] = [];

    const flushBotMessages = (keyPrefix: string) => {
      if (combinedBotMessages.length === 0) return;

      result.push(
        <div key={`${keyPrefix}-group`} className="iris-chat-bubble iris-bot">
          <span
            dangerouslySetInnerHTML={{
              __html: formatBotResponseHTML(
                combinedBotMessages.join(""),
                isThinking || isLoading,
              ),
            }}
          />
          {/* {lastBotButtons.length > 0 && (
            <div
              className="iris-button-class-container--blk"
              style={{ marginTop: "-5px" }}
            >
              {lastBotButtons.map((btn, i) => (
                <button
                  key={i}
                  className="iris-button-class"
                  disabled={isThinking || isLoading}
                  style={
                    isThinking || isLoading
                      ? { opacity: 0.5, cursor: "not-allowed" }
                      : undefined
                  }
                >
                  {btn.text}
                </button>
              ))}
            </div>
          )} */}
          {lastFeedbackButtons.length > 0 && (
            <div
              className="iris-feedback-container"
              style={{
                marginTop: "-20px",
                display: "flex",
                gap: "16px",
              }}
            >
              {lastFeedbackButtons.map((btn, i) => (
                <button
                  key={i}
                  className="iris-button-class"
                  disabled={isThinking || isLoading}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    opacity: isThinking || isLoading ? 0.5 : undefined,
                    cursor: isThinking || isLoading ? "not-allowed" : undefined,
                  }}
                >
                  {btn.text === "Helpful" ? <LightBulb /> : <File />} {btn.text}
                </button>
              ))}
            </div>
          )}
        </div>,
      );
      combinedBotMessages = [];
      lastBotButtons = [];
      lastFeedbackButtons = [];
    };

    messages.forEach((msg, idx) => {
      if (msg.sender === "user") {
        flushBotMessages(`bot-${idx}`);

        const isLastUser =
          idx === messages.length - 1 ||
          messages.slice(idx + 1).every((m) => m.sender !== "user");

        result.push(
          <div
            key={`user-${idx}`}
            className="iris-chat-bubble iris-user"
            ref={isLastUser ? lastUserRef : null}
          >
            {msg.userInput}
          </div>,
        );
      } else if (msg.sender === "bot" || msg.sender === "assistant") {
        combinedBotMessages.push(msg.userInput);
        if (msg.buttons && msg.buttons.length > 0) {
          lastBotButtons = msg.buttons;
        }
        if (msg.feedbackButtons && msg.feedbackButtons.length > 0) {
          lastFeedbackButtons = msg.feedbackButtons;
        }
      }
    });

    flushBotMessages(`bot-${messages.length}-final`);

    return result;
  }, [messages, isThinking, isLoading]);

  /* ---------- Bot activity indicator ---------- */
  const renderBotIndicator = () => {
    if (!isThinking && !isLoading) return null; // No indicator if nothing is happening

    return (
      <div
        className={`iris-chat-bubble iris-bot typing-indicator ${
          isThinking ? "thinking-indicator" : "isLoading-bot"
        }`}
      >
        {isThinking && <span className="thinking-label">Typing</span>}
        <span className="dot" />
        <span className="dot" />
        <span className="dot" />
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`iris-chat-history ${
        messages.length === 0 ? "" : "iris-chat-history-active"
      }`}
      onClick={(e) => {
        handleClick(e);
        handleHeaderClick(e);
      }}
    >
      {renderedMessages}
      {renderBotIndicator()}
    </div>
  );
};

export default ChatHistory;
